/**
 * Public shared catalog view: /store/catalog/:token.
 * Reuses QuotationAcceptanceView (cotizado+) or QuotationBlocksView (pre-cotizado) for consistent UI.
 * Data from GET /api/quotation-request/by-token (optional Authorization for restricted links).
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { StoreBreadcrumbs } from '@/components/store/StoreBreadcrumbs';
import { LogIn, ShoppingCart, Loader2 } from 'lucide-react';
import type { QuotationRequest, QuotationRequestItem, QuotationRequestStatus } from '@/lib/store-types';
import type { QuotationCartLine } from '@/hooks/use-quotation-cart';
import { toCartLine } from '@/hooks/use-quotation-cart';
import { getWorkerApiBase } from '@/lib/worker-api';
import { supabase } from '@/lib/supabase';
import { useSourcingItems, useBrands, useOrigins, useMoqUnits, useLoadingMethods } from '@/hooks/use-store';
import { QuotationAcceptanceView } from '@/components/store/QuotationAcceptanceView';
import { QuotationBlocksView } from '@/components/store/QuotationBlocksView';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

/** Statuses that show the full QuotationAcceptanceView (cotizado or any post-accept phase). */
const COTIZADO_PLUS: QuotationRequestStatus[] = [
  'cotizado', 'finished', 'pending_deposit_payment', 'in_process',
  'shipped', 'pending_final_payment', 'arriving_soon', 'delivered',
];

function orderNumber(id: string) {
  return id.replace(/-/g, '').slice(0, 8).toUpperCase();
}

export default function StoreCatalogShared() {
  const { token: tokenParam } = useParams<{ token: string }>();
  const token = tokenParam ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [request, setRequest] = useState<QuotationRequest | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | '404' | '403'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [reclaimPending, setReclaimPending] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('404');
      setErrorMessage('Falta el token del enlace.');
      return;
    }
    let cancelled = false;
    (async () => {
      const base = getWorkerApiBase();
      const headers: Record<string, string> = {};
      const { data: session } = await supabase.auth.getSession();
      if (session?.session?.access_token) {
        headers['Authorization'] = `Bearer ${session.session.access_token}`;
      }
      const res = await fetch(
        `${base}/api/quotation-request/by-token?token=${encodeURIComponent(token)}`,
        { headers }
      );
      if (cancelled) return;
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) {
        setStatus('404');
        setErrorMessage((data?.error as string) || 'Enlace no encontrado.');
        return;
      }
      if (res.status === 403) {
        setStatus('403');
        setErrorMessage((data?.error as string) || 'No tienes permiso para ver este enlace.');
        return;
      }
      if (!res.ok) {
        setStatus('404');
        setErrorMessage((data?.error as string) || 'Error al cargar.');
        return;
      }
      setRequest(data as QuotationRequest);
      setStatus('ok');
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const { data: allSourcingItems } = useSourcingItems(null);
  const { data: brands } = useBrands();
  const { data: origins } = useOrigins();
  const { data: moqUnits } = useMoqUnits();
  const { data: loadingMethods } = useLoadingMethods();

  const items = (request?.items ?? []) as QuotationRequestItem[];
  const cartLines = useMemo(() => {
    if (!items.length) return [];
    return items.map((it) =>
      toCartLine(
        { ...it, moq_value: (it as { moq_value?: number }).moq_value },
        allSourcingItems ?? [],
        brands ?? [],
        origins ?? [],
        moqUnits ?? []
      )
    );
  }, [items, allSourcingItems, brands, origins, moqUnits]);

  // Reclamar = duplicar la cotización para el lead logueado, preservando estado.
  const handleReclaim = useCallback(async () => {
    if (!token) return;
    setReclaimPending(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        localStorage.setItem('pendingReclaimToken', token);
        navigate(`/store/catalog/${token}/reclaim`);
        return;
      }
      const base = getWorkerApiBase();
      const res = await fetch(`${base}/api/quotation-request/reclaim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.session.access_token}`,
        },
        body: JSON.stringify({ token }),
      });
      const data = (await res.json().catch(() => ({}))) as { request_id?: string; error?: string };
      if (res.ok && data.request_id) {
        localStorage.removeItem('pendingReclaimToken');
        queryClient.invalidateQueries({ queryKey: ['quotation-request-cart'] });
        toast({ title: 'Cotización reclamada', description: 'Se ha creado una copia en tu cuenta.' });
        navigate(`/store/quotations/${data.request_id}`, { replace: true });
      } else {
        toast({ title: data.error ?? 'Error al reclamar', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error al reclamar la cotización', variant: 'destructive' });
    } finally {
      setReclaimPending(false);
    }
  }, [token, navigate, queryClient, toast]);

  const reqStatus = (request?.status ?? 'draft') as QuotationRequestStatus;
  const isCotizadoPlus = COTIZADO_PLUS.includes(reqStatus);
  const isPostAccept = reqStatus !== 'cotizado' && reqStatus !== 'finished' && isCotizadoPlus;

  const breadcrumbs = [
    { label: 'Store', path: '/store' },
    { label: 'Catálogo compartido' },
  ];

  if (status === 'loading') {
    return (
      <div className="space-y-6">
        <StoreBreadcrumbs segments={breadcrumbs} />
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400 mx-auto mb-3" />
          <p className="text-zinc-600">Cargando catálogo...</p>
        </div>
      </div>
    );
  }

  if (status === '403') {
    return (
      <div className="space-y-6">
        <StoreBreadcrumbs segments={breadcrumbs} />
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center max-w-md mx-auto">
          <p className="text-amber-900 mb-4">{errorMessage}</p>
          <p className="text-sm text-amber-800 mb-4">Inicia sesión con la cuenta asociada a esta cotización para verla.</p>
          <Button asChild className="gap-2">
            <Link to="/store/login" state={{ from: `/store/catalog/${token}` }}>
              <LogIn className="h-4 w-4" />
              Iniciar sesión
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  if (status === '404' || !request) {
    return (
      <div className="space-y-6">
        <StoreBreadcrumbs segments={breadcrumbs} />
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-8 text-center max-w-md mx-auto">
          <p className="text-zinc-700">{errorMessage || 'Enlace no encontrado o no válido.'}</p>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/store">Ir al catálogo</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StoreBreadcrumbs segments={breadcrumbs} />

      {/* Vista principal: misma que store/cart y admin detail */}
      {isCotizadoPlus ? (
        <QuotationAcceptanceView
          requestId={request.id}
          blocks={request.blocks}
          items={cartLines}
          status={reqStatus}
          lead={request.lead ?? undefined}
          incoterm={request.incoterm}
          destinationPortOrAddress={request.destination_port_or_address}
          addressee_company={request.addressee_company}
          addressee_contact={request.addressee_contact}
          final_price_text={request.final_price_text}
          payment_type={request.payment_type}
          payment_method={request.payment_method}
          observaciones={request.observaciones}
          documentTitle={request.document_title}
          headerImageUrl={request.header_image_url}
          sealImageUrl={request.seal_image_url}
          finalMessage={request.final_message}
          showFinalMessage={request.show_final_message}
          quotationCode={orderNumber(request.id)}
          showActions={false}
          responded={reqStatus === 'finished' || isPostAccept}
          outcome={request.outcome ?? undefined}
          allSourcingItems={allSourcingItems ?? []}
          loadingMethods={loadingMethods ?? []}
        />
      ) : (
        <QuotationBlocksView
          request={{ blocks: request.blocks, status: reqStatus }}
          items={cartLines}
          mode="admin-view"
          readOnly
          adminProps={{
            allSourcingItems: allSourcingItems ?? [],
            loadingMethods: loadingMethods ?? [],
          }}
        />
      )}

      {/* CTA: reclamar cotización */}
      <div className="flex flex-wrap gap-4 pt-2">
        <Button size="lg" className="gap-2" disabled={reclaimPending} onClick={handleReclaim}>
          {reclaimPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ShoppingCart className="h-4 w-4" />
          )}
          Reclamar esta cotización
        </Button>
        <Button asChild variant="outline" size="lg">
          <Link to="/store">Explorar catálogo</Link>
        </Button>
      </div>
    </div>
  );
}
